/**
 * Shared in-product option-radio listener for the V2 PDP.
 *
 * Owns every radio input carrying `data-v2-option-input` on the page —
 * not just Configuration (which has no in-product radios of its own,
 * since switching Configuration is a full navigation to a sibling
 * product; see snippets/configuration-picker-v2.liquid's {% doc %}).
 * Fabric/colour (this file's hardening task) and arm (later task) pickers
 * render radios with this attribute and add no JS of their own — this
 * file is the single owner of the change-handling logic for all of them.
 *
 * Data source: sections/main-product-v2.liquid renders three JSON script
 * tags once per section, unconditionally on product.variants.size (moved
 * out of snippets/configuration-picker-v2.liquid in the final review pass
 * so this data — and therefore every fabric/colour/arm radio on the page
 * — no longer silently disappears on a page with no configuration_picker
 * block):
 *   - [data-v2-variants]        -> product.variants (full Shopify variant objects)
 *   - [data-v2-price-map]       -> { variantId: { price: "formatted money string",
 *     compare: "formatted money string" | "" } } — compare is "" when that
 *     variant isn't on sale. Older cached markup may still emit the
 *     pre-review string-only format ({ variantId: "formatted money
 *     string" }); updatePrice below guards for both.
 *   - [data-v2-current-variant] -> product.selected_or_first_available_variant
 *
 * On change of any data-v2-option-input radio:
 *   1. Read every currently-checked data-v2-option-input radio's
 *      data-option-position/data-option-value into a { position: value }
 *      map (position is the option's 1-based index, matching Shopify's
 *      option1/2/3 numbering — see findMatchingVariant below for why
 *      position, not name, is the match key).
 *   2. Fill in any option position with no checked radio (an option whose
 *      picker block isn't rendered on the page, or that renders a single
 *      value as a non-radio tile — e.g. configuration-picker-v2's
 *      "EXACTLY ONE local value" branch) from data-v2-current-variant, so
 *      partial option coverage still resolves to a real variant.
 *   3. Find the variant in data-v2-variants whose option1/2/3 match every
 *      resolved position/value pair.
 *   4. Dispatch `document` CustomEvent `variant:change` with
 *      `detail: { variant }` (consumed by product-gallery-v2 and others).
 *   5. Update `input[name="id"]` in the enclosing product form, if present.
 *   6. Update `[data-price-container]`'s current/compare price text using
 *      the pre-rendered price map (never Shopify.formatMoney) — see
 *      updatePrice below.
 */
(function () {
  // Idempotency guard: this file is the single owner of the shared
  // data-v2-option-input listener. If it ever gets loaded twice (e.g. a
  // snippet re-adding its own script tag), bail rather than double-binding.
  if (window.__v2ConfigurationPickerBound) return;
  window.__v2ConfigurationPickerBound = true;

  function getVariantsData() {
    var el = document.querySelector('[data-v2-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  function getPriceMap() {
    var el = document.querySelector('[data-v2-price-map]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function getCurrentVariant() {
    var el = document.querySelector('[data-v2-current-variant]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Keyed by data-option-position (Shopify's 1-based option1/2/3 index),
  // not data-option-name: multiple pickers only ever cover a subset of a
  // product's options, and position is the stable, name-independent key
  // shared with variant.option1/2/3.
  function getCheckedOptionsByPosition() {
    var checked = document.querySelectorAll('[data-v2-option-input]:checked');
    var selections = {};
    checked.forEach(function (input) {
      var position = input.dataset.optionPosition;
      var value = input.dataset.optionValue;
      if (position && value) selections[position] = value;
    });
    return selections;
  }

  // Merges checked radio selections with the current variant's own
  // option1/2/3 so that option positions with no rendered/checked radio
  // (a picker block not yet on the page, or a single-value option
  // rendered as a static tile rather than a radio) still resolve to a
  // value instead of being dropped from matching entirely.
  function resolveTargetValuesByPosition(selectionsByPosition, currentVariant) {
    var resolved = {};
    ['1', '2', '3'].forEach(function (position) {
      if (selectionsByPosition[position]) {
        resolved[position] = selectionsByPosition[position];
      } else if (currentVariant && currentVariant['option' + position]) {
        resolved[position] = currentVariant['option' + position];
      }
    });
    return resolved;
  }

  function findMatchingVariant(variants, targetValuesByPosition) {
    var positions = Object.keys(targetValuesByPosition);
    if (positions.length === 0) return null;
    return (
      variants.find(function (variant) {
        return positions.every(function (position) {
          return variant['option' + position] === targetValuesByPosition[position];
        });
      }) || null
    );
  }

  // Lexicographic compare of two equal-length numeric score arrays.
  // Returns >0 if a ranks higher than b, <0 if lower, 0 if equal.
  function compareScore(a, b) {
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return a[i] - b[i];
    }
    return 0;
  }

  // Fallback for when no variant matches every resolved option position — e.g.
  // an option value whose variants live in a disjoint slice of another option's
  // values (Arm Type "Armless" only pairs with the Ottoman / Armless Module /
  // Armless Chaise sizes, none of which overlap Low/High's sizes). Without this,
  // switching to such a value resolves to no variant and handleChange bails
  // silently, so the picker appears dead.
  //
  // Snaps to the nearest variant that HONOURS the just-changed option
  // (requiredPosition — the position the user actually clicked, which must be
  // matched exactly), relaxing the other options as needed. Candidates are
  // ranked by: availability first, then matching each other option position in
  // ascending position order (so a lower-numbered option — e.g. Colour at
  // position 2 — is preserved in preference to a higher-numbered one like Size
  // at position 3). The server-side section refetch then re-renders every picker
  // to reflect the variant we land on, so the relaxed option's UI updates too.
  function findNearestVariant(variants, targetValuesByPosition, requiredPosition) {
    if (!requiredPosition) return null;
    var requiredValue = targetValuesByPosition[requiredPosition];
    if (!requiredValue) return null;
    var otherPositions = ['1', '2', '3'].filter(function (position) {
      return position !== requiredPosition && targetValuesByPosition[position];
    });
    var best = null;
    var bestScore = null;
    variants.forEach(function (variant) {
      if (variant['option' + requiredPosition] !== requiredValue) return;
      var score = [variant.available ? 1 : 0];
      otherPositions.forEach(function (position) {
        score.push(variant['option' + position] === targetValuesByPosition[position] ? 1 : 0);
      });
      if (bestScore === null || compareScore(score, bestScore) > 0) {
        bestScore = score;
        best = variant;
      }
    });
    return best;
  }

  function updateForm(variant) {
    document.querySelectorAll('form[data-v2-product-form] input[name="id"]').forEach(function (input) {
      input.value = variant.id;
    });
  }

  function updatePrice(variant, priceMap) {
    var entry = priceMap[String(variant.id)];
    if (!entry) return;

    // Backward-compat guard: the price map used to be a plain
    // { variantId: "formatted string" } map before the final review pass
    // added compare-at support. Handle both shapes so a partially cached
    // page (new JS, old SSR markup) doesn't break.
    var priceText;
    var compareText;
    if (typeof entry === 'string') {
      priceText = entry;
      compareText = '';
    } else {
      priceText = entry.price;
      compareText = entry.compare || '';
    }

    document.querySelectorAll('[data-price-container]').forEach(function (container) {
      var current = container.querySelector('.ph-v2__price-current');
      var compare = container.querySelector('.ph-v2__price-compare');

      if (current) {
        current.textContent = priceText;
        current.classList.toggle('v2-price-sale', !!compareText);
      } else {
        container.textContent = priceText;
      }

      if (compare) {
        if (compareText) {
          compare.textContent = compareText;
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }
    });
  }

  function updateSelectedLabel(input) {
    var name = input.dataset.optionName;
    var value = input.dataset.optionValue;
    if (!name || !value) return;
    document.querySelectorAll('[data-v2-selected-label]').forEach(function (label) {
      if (label.dataset.optionName === name) label.textContent = value;
    });
  }

  // Chaise-orientation note is only relevant to configurations that actually
  // have a (swappable) chaise. Show it when the selected config value contains
  // "chaise", hide it otherwise — L-shape configs have no chaise, so they don't
  // get the note. (SSR sets the initial state; this keeps it correct on
  // client-side switches of a same-product config, where no page reload
  // happens.) Keep this in sync with the SSR condition in
  // snippets/configuration-picker-v2.liquid.
  function updateNote(input) {
    var value = (input.dataset.optionValue || '').toLowerCase();
    var showNote = value.indexOf('chaise') !== -1;
    document.querySelectorAll('[data-v2-config-note]').forEach(function (note) {
      note.hidden = !showNote;
    });
  }

  function getVariantById(id) {
    var variants = getVariantsData();
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return null;
  }

  // Loader on the price while a variant refetch is in flight.
  function toggleLoader(on) {
    document.querySelectorAll('[data-price-container]').forEach(function (el) {
      el.classList.toggle('v2-price--loading', on);
    });
  }

  // The mounted delivery widget only reads data-line-item once, at mount; after
  // that it re-quotes on the theme pubsub 'variant-change' event (assets/
  // pubsub.js), reading data.variant.{id, sku, name, price, featured_image.
  // product_id}. It throws if featured_image is null (variants without their
  // own image), so pass the product id through explicitly.
  function publishDeliveryVariant(variant) {
    if (typeof window.publish !== 'function') return;
    var productId = variant.featured_image && variant.featured_image.product_id;
    if (!productId) {
      var mount = document.getElementById('DeliveryDetails');
      try {
        productId = JSON.parse(mount.dataset.lineItem).productId.replace('gid://shopify/Product/', '');
      } catch (e) {
        return;
      }
    }
    window.publish('variant-change', {
      data: {
        variant: Object.assign({}, variant, {
          featured_image: Object.assign({}, variant.featured_image, { product_id: productId })
        })
      }
    });
  }

  // Colour / arm change: re-render the product section for the new variant via
  // the Section Rendering API and swap it in, so price + gallery + pickers all
  // reflect the variant (server-rendered). No full reload; loader on the price.
  function refetchVariant(variantId) {
    var section = document.querySelector('.mp-v2[data-section-id]');
    var sectionId = section && section.getAttribute('data-section-id');
    var target = window.location.pathname + '?variant=' + variantId;
    if (!sectionId) { window.location.href = target; return; }
    toggleLoader(true);
    fetch(target + '&section_id=' + encodeURIComponent(sectionId))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.mp-v2');
        var live = document.querySelector('.mp-v2');
        if (!fresh || !live) { window.location.href = target; return; }
        // Preserve the already-mounted delivery widget across the swap. The
        // delivery-estimates.js bundle self-mounts its React app into
        // #DeliveryDetails ONCE at page load and is never re-run for the
        // replaced section, so the fresh markup's empty #DeliveryDetails would
        // leave only the disabled placeholder input (can't type). Move the live
        // mounted node into the fresh tree (carrying the new variant's
        // data-line-item), keeping the mounted app alive. Detect the mount via
        // React's container marker rather than children, so a root that hasn't
        // rendered yet is still carried over. If the bundle hasn't run yet, the
        // fresh node stays and the bundle mounts into it when it does.
        var liveDelivery = live.querySelector('#DeliveryDetails');
        var freshDelivery = fresh.querySelector('#DeliveryDetails');
        var deliveryMounted = !!liveDelivery && Object.keys(liveDelivery).some(function (key) {
          return key.indexOf('__reactContainer') === 0;
        });
        if (liveDelivery && freshDelivery && (deliveryMounted || liveDelivery.childElementCount > 0)) {
          liveDelivery.dataset.lineItem = freshDelivery.dataset.lineItem || liveDelivery.dataset.lineItem;
          freshDelivery.replaceWith(liveDelivery);
        }
        // Preserve scroll across the swap: the fresh section's images/media are
        // unloaded on insert, so it's momentarily short and the browser clamps
        // the scroll to the top. Restore now and again after layout settles.
        var scrollY = window.scrollY;
        live.replaceWith(fresh); // re-inits custom elements; delegated listeners survive
        window.scrollTo(0, scrollY);
        requestAnimationFrame(function () { window.scrollTo(0, scrollY); });
        var v = getVariantById(variantId);
        if (v) document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: v } }));
        if (v) publishDeliveryVariant(v);
        if (window.history && window.history.replaceState) window.history.replaceState({}, '', target);
        toggleLoader(false);
      })
      .catch(function () { window.location.href = target; });
  }

  function handleChange(event) {
    var input = event.target.closest('[data-v2-option-input]');
    if (!input) return;

    var targetValues = resolveTargetValuesByPosition(getCheckedOptionsByPosition(), getCurrentVariant());
    var variants = getVariantsData();
    var variant = findMatchingVariant(variants, targetValues);
    if (!variant) {
      variant = findNearestVariant(variants, targetValues, input.dataset.optionPosition);
    }
    if (!variant) return;

    // Configuration change → full page reload (bigger change: layout/product).
    // Colour / arm change → partial section refetch (Section Rendering API).
    if (input.closest('[data-configuration-picker]')) {
      window.location.href = window.location.pathname + '?variant=' + variant.id;
      return;
    }
    updateSelectedLabel(input); // instant label feedback while the refetch runs
    refetchVariant(variant.id);
  }

  // Variants in the product's options.hidden_variants metafield are left out of
  // data-v2-variants (sections/main-product-v2.liquid). If the server landed on
  // one anyway — Shopify's first-available default, or a ?variant= link to it —
  // move to the nearest visible variant, keeping option1 (then option2, ...)
  // where possible.
  function redirectFromHiddenVariant() {
    var current = getCurrentVariant();
    var variants = getVariantsData();
    if (!current || !variants.length) return;
    if (getVariantById(current.id)) return;

    var targetValues = resolveTargetValuesByPosition({}, current);
    var variant =
      findNearestVariant(variants, targetValues, '1') ||
      variants.find(function (v) { return v.available; }) ||
      variants[0];
    if (variant) refetchVariant(variant.id);
  }

  document.addEventListener('change', handleChange);
  redirectFromHiddenVariant();
})();
